/**
 * «Распределение» → «Сделки» → «Сделки в работе» (`?kind=deals&tab=inwork`):
 * сделки стадии «В работе» по дню последнего звонка, его итогу и дате следующей
 * обработки. Цель — видеть, кого вчера не дозвонились и сегодня ещё не звонили,
 * чтобы раздать их на повторный звонок. Спека `docs/specs/2026-09-30-deals-inwork-design.md`;
 * числа — те же, что у `prokat-runner deals-inwork`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { type DealsInwork as Data, type InworkState, loadDealsInwork } from "@/lib/dealInwork";
import { shortTime } from "@/lib/leads";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "whitespace-nowrap px-3 py-1.5 text-right font-medium";
const td = "px-3 py-1.5 text-right tabular-nums";

type Pick = { row?: string; col?: string; state?: InworkState };
type StateOrAll = "all" | InworkState;

function Num({ n, on, active }: { n: number; on: () => void; active: boolean }) {
  if (!n) return <span className="text-fg-faint">·</span>;
  return (
    <button
      type="button"
      onClick={on}
      className={`tabular-nums underline decoration-border underline-offset-2 hover:decoration-fg ${
        active ? "rounded bg-accent/15 px-1 font-medium" : ""
      }`}
      title="Показать сделки"
    >
      {n}
    </button>
  );
}

const same = (a: Pick, b: Pick) => a.row === b.row && a.col === b.col && a.state === b.state;

function ByLastCall({ d, pick, setPick }: { d: Data; pick: Pick; setPick: (p: Pick) => void }) {
  const label = Object.fromEntries(d.rows.map((r) => [r.key, r.label]));
  return (
    <section className={card} data-annot="в работе по последнему звонку">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Последний звонок и его итог</h2>
        <span className="text-[12px] text-fg-faint">
          день последнего исходящего; итог — за тот день: разговор (любой звонок), соединились без разметки, иначе
          недозвон
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Последний звонок</th>
              <th className={th}>Всего</th>
              {d.states
                .filter((s) => s.key !== "none")
                .map((s) => (
                  <th key={s.key} className={th}>
                    {s.label}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {d.by_row.map((r) => (
              <tr key={r.key} className="border-t border-border hover:bg-bg-hover" data-annot={`в работе ${r.key}`}>
                <td className="whitespace-nowrap px-4 py-1.5">{label[r.key]}</td>
                <td className={`${td} font-medium`}>
                  <Num n={r.total} active={same(pick, { row: r.key })} on={() => setPick({ row: r.key })} />
                </td>
                {d.states
                  .filter((s) => s.key !== "none")
                  .map((s) => (
                    <td key={s.key} className={td}>
                      <Num
                        n={r[s.key]}
                        active={same(pick, { row: r.key, state: s.key })}
                        on={() => setPick({ row: r.key, state: s.key })}
                      />
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Matrix({ d, pick, setPick }: { d: Data; pick: Pick; setPick: (p: Pick) => void }) {
  const [state, setState] = useState<StateOrAll>("no_answer");
  const m = d.matrix[state];
  const st = state === "all" ? undefined : state;
  const chips: { key: StateOrAll; label: string }[] = [{ key: "all", label: "все" }, ...d.states];
  return (
    <section className={card} data-annot="в работе матрица">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Последний звонок × следующая обработка</h2>
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Итог последнего звонка">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              role="radio"
              aria-checked={state === c.key}
              onClick={() => setState(c.key)}
              className={`rounded-full border px-2.5 py-0.5 text-[12px] ${
                state === c.key ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Последний звонок</th>
              {d.cols.map((c) => (
                <th key={c.key} className={th}>
                  {c.label}
                </th>
              ))}
              <th className={th}>Итого</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const total = d.cols.reduce((s, c) => s + (m[r.key]?.[c.key] ?? 0), 0);
              return (
                <tr key={r.key} className="border-t border-border hover:bg-bg-hover">
                  <td className="whitespace-nowrap px-4 py-1.5">{r.label}</td>
                  {d.cols.map((c) => {
                    const p = { row: r.key, col: c.key, ...(st ? { state: st } : {}) };
                    return (
                      <td key={c.key} className={td}>
                        <Num n={m[r.key]?.[c.key] ?? 0} active={same(pick, p)} on={() => setPick(p)} />
                      </td>
                    );
                  })}
                  <td className={`${td} font-medium`}>{total || <span className="text-fg-faint">·</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        следующая обработка — поле «ждём клиента / след. обработка» (`waitShowroom`) сейчас
      </div>
    </section>
  );
}

function DealList({ d, pick }: { d: Data; pick: Pick }) {
  const rows = d.deals.filter(
    (x) =>
      (!pick.row || x.row === pick.row) && (!pick.col || x.col === pick.col) && (!pick.state || x.state === pick.state),
  );
  const rowL = Object.fromEntries(d.rows.map((r) => [r.key, r.label]));
  const colL = Object.fromEntries(d.cols.map((r) => [r.key, r.label]));
  const stL = Object.fromEntries(d.states.map((r) => [r.key, r.label]));
  const title = [
    pick.row && `звонок: ${rowL[pick.row]}`,
    pick.state && stL[pick.state],
    pick.col && `обработка: ${colL[pick.col]}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <section className={card} data-annot="в работе список">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Сделки: {rows.length}</h2>
        <span className="text-[12px] text-fg-faint">{title || "все"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Нет сделок.</div>
      ) : (
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-bg-subtle text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1 font-medium">Сделка</th>
                <th className="px-4 py-1 font-medium">Ответственный</th>
                <th className="px-4 py-1 font-medium">Салон</th>
                <th className="px-4 py-1 font-medium">Последний звонок</th>
                <th className="px-4 py-1 text-right font-medium">Попыток в тот день</th>
                <th className="px-4 py-1 font-medium">Итог</th>
                <th className="px-4 py-1 font-medium">След. обработка</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 500).map((x) => (
                <tr key={x.id} className="border-t border-border hover:bg-bg-hover">
                  <td className="px-4 py-1.5 font-mono tabular-nums">
                    <Link to={`/deals/${x.id}`} className="underline">
                      {x.id}
                    </Link>
                  </td>
                  <td className="px-4 py-1.5">{x.user ?? <span className="text-fg-faint">—</span>}</td>
                  <td className="px-4 py-1.5 text-fg-muted">{x.location ?? "—"}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                    {x.last_call_at ? shortTime(x.last_call_at) : <span className="text-fg-faint">—</span>}
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{x.attempts || "·"}</td>
                  <td className={`px-4 py-1.5 ${x.state === "talk" ? "" : "text-fg-muted"}`}>{stL[x.state]}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                    {x.wait_at ? shortTime(x.wait_at) : <span className="text-fg-faint">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 500 && (
            <div className="px-4 py-2 text-[11.5px] text-fg-faint">показаны первые 500 — уточните клетку</div>
          )}
        </div>
      )}
    </section>
  );
}

export function DealsInwork() {
  const q = useQuery({ queryKey: ["deals-inwork"], queryFn: loadDealsInwork, refetchInterval: 5 * 60_000 });
  // По умолчанию — те, кого вчера не дозвонились и сегодня не звонили: кандидаты на повтор.
  const [pick, setPick] = useState<Pick>({ row: "d1", state: "no_answer" });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю сделки в работе…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить сделки в работе: {q.error.message}</div>;
  const d = q.data;
  return (
    <>
      <section className={`${card} flex flex-wrap items-start gap-x-8 gap-y-3 p-4`} data-annot="в работе итог">
        <div>
          <div className="text-[12px] text-fg-muted">В работе</div>
          <div className="text-[26px] font-semibold leading-none tabular-nums">{d.total}</div>
          <div className="mt-1 text-[11.5px] text-fg-faint">
            стадия «В работе» в CRM · {shortTime(d.now)}
            {d.unknown > 0 && ` · без контактов в копии ${d.unknown}`}
          </div>
        </div>
        <div>
          <div className="text-[12px] text-fg-muted">Вчера не дозвонились, сегодня не звонили</div>
          <button
            type="button"
            onClick={() => setPick({ row: "d1", state: "no_answer" })}
            className="text-[26px] font-semibold leading-none tabular-nums underline decoration-border underline-offset-4 hover:decoration-fg"
          >
            {d.redial.total}
          </button>
          <div className="mt-1 text-[11.5px] text-fg-faint">
            след. обработка:{" "}
            {d.cols
              .filter((c) => d.redial.by_col[c.key])
              .map((c) => `${c.label} ${d.redial.by_col[c.key]}`)
              .join(" · ") || "—"}
          </div>
        </div>
        <div className="max-w-md text-[11.5px] text-fg-faint">
          Звонки — наша копия Bitrix с {d.since.split("-").reverse().join(".")}: «не звонили» значит не звонили с этой
          даты. Сделки, кому вчера не дозвонились, можно раздать на повторный звонок, когда закончится обзвон.
        </div>
      </section>
      <ByLastCall d={d} pick={pick} setPick={setPick} />
      <Matrix d={d} pick={pick} setPick={setPick} />
      <DealList d={d} pick={pick} />
    </>
  );
}
